import { useCallback, useMemo } from 'react';
import { useRouter } from '@tanstack/react-router';
import { useDocumentParams } from 'app/router/params';

import { TagWithMetadata } from 'entities/tag';
import { usePermissions } from 'app/api/permission-queries';
import { useCreateAccessLog } from 'entities/access-log';
import { useEditorContext } from 'app/state/contexts/EditorContext';
import { useSelectedOrganization } from 'app/state/contexts/UserContext';
import { resolveGoToTagAction, findTagDomNode, scrollToNode, blinkTagNode, GoToTagAction } from 'app/utils/tag-utils';
import { i18n } from 'shared/i18n';
import { useEditorLiveSection } from 'app/state/contexts/EditorUiContext';

const MAX_RESOLVE_FRAMES = 10;

export interface GoToTagState {
  kind: GoToTagAction['kind'];
  disabled: boolean;
  tooltip?: string;
}

export const useGoToTag = () => {
  const { documentId } = useDocumentParams();
  const router = useRouter();
  const { sectionPermissions } = usePermissions(documentId);
  const permissions = useMemo(() => (sectionPermissions ?? {}) as Record<string, unknown>, [sectionPermissions]);
  const { mutate: createAccessLog } = useCreateAccessLog();
  const section = useEditorLiveSection();
  const organizationId = useSelectedOrganization();
  const ctx = useEditorContext();
  const shadowHost = ctx?.shadowHost;

  const currentSectionId = section?.superId ?? null;

  const scrollAndBlink = useCallback(
    (tag: Pick<TagWithMetadata, 'id' | 'onId'>) => {
      const root: ParentNode | null = shadowHost?.shadowRoot ?? (typeof document !== 'undefined' ? document : null);
      if (!root) return;
      let frames = 0;
      const attempt = () => {
        const node = findTagDomNode(root, { id: tag.id, onId: tag.onId });
        if (node) {
          scrollToNode(node);
          blinkTagNode(node);
          return;
        }
        if (frames++ < MAX_RESOLVE_FRAMES) requestAnimationFrame(attempt);
      };
      attempt();
    },
    [shadowHost]
  );

  const goToTag = useCallback(
    (tag: TagWithMetadata) => {
      const action = resolveGoToTagAction(tag, currentSectionId, permissions);
      if (action.kind === 'blocked') return;
      if (action.kind === 'scroll') {
        scrollAndBlink(tag);
        return;
      }
      createAccessLog({ sectionSuperId: action.sectionSuperId, documentSuperId: documentId, organizationId });
      const target = router.buildLocation({
        to: '/documents/$documentId/sections/$sectionId',
        params: { documentId, sectionId: action.sectionSuperId },
        search: { taggingMode: true, goToTagId: tag.id, goToTagOnId: tag.onId ?? undefined },
        hash: tag.pageId ?? undefined,
      });
      window.location.href = target.href;
    },
    [currentSectionId, permissions, scrollAndBlink, createAccessLog, organizationId, documentId, router]
  );

  const getGoToTagState = useCallback(
    (tag: TagWithMetadata): GoToTagState => {
      const action = resolveGoToTagAction(tag, currentSectionId, permissions);
      if (action.kind === 'blocked') {
        return {
          kind: action.kind,
          disabled: true,
          tooltip: action.reason === 'hidden' ? i18n('tag:go.to.tag.hidden') : i18n('tag:go.to.tag.no.access'),
        };
      }
      return { kind: action.kind, disabled: false };
    },
    [currentSectionId, permissions]
  );

  return { goToTag, getGoToTagState };
};
